import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Search, Save, RefreshCw, ChevronLeft, ChevronRight } from "lucide-react";

const TaxMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = useMemo(() => `bg-${baseColor}-${baseShade}`, [baseColor, baseShade]);
  const hoverClass = useMemo(() => `hover:bg-${baseColor}-700`, [baseColor]);

  // IMPORTANT: match your infra approach (you used finYearId in login)
  // If your server expects x-finyear like "2025-2026", store that string in localStorage.
  const finYear = (localStorage.getItem("finYear") || "").trim();
  const headers = useMemo(() => (finYear ? { "x-finyear": finYear } : {}), [finYear]);

  const [taxName, setTaxName] = useState("");

  // search + pagination
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const recordsPerPage = 20;
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchList = async (p = 1, q = debouncedSearch) => {
    try {
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/tax-master/list`, {
        params: { page: p, limit: recordsPerPage, search: q || "" },
        headers,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load tax list");
        return;
      }

      setRows(res.data.list || []);
      setPage(res.data.page || p);
      setTotalPages(res.data.totalPages || 1);
      setTotalRecords(res.data.total || 0);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load tax list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(1, debouncedSearch);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const validate = () => {
    if (!taxName.trim()) {
      toast.error("Please enter Tax Name..");
      return false;
    }
    if (!taxName.replace(/\s/g, "").length) {
      toast.error("Please enter Tax Name..");
      return false;
    }
    return true;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      setSaving(true);

      const res = await axios.post(
        `${BASE_URL}/api/tax-master/create`,
        { taxName: taxName.trim() },
        { headers }
      );

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed");
        return;
      }

      toast.success(res.data?.message || "Successfully Inserted");
      setTaxName("");
      await fetchList(1, debouncedSearch);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  const pageButtons = useMemo(() => {
    const arr = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) arr.push(i);
      return arr;
    }

    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);

    for (let i = start; i <= end; i++) arr.push(i);
    if (end < totalPages) arr.push("...");

    return arr;
  }, [page, totalPages]);

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">Tax Master</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top row */}
          <div className="flex justify-between items-center gap-3 flex-wrap mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Tax Master
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <button
              type="button"
              onClick={() => {
                setTaxName("");
                setSearch("");
                fetchList(1, "");
                toast.info("Refreshed");
              }}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow hover:shadow-md transition ${
                isDark
                  ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                  : "bg-gray-100 hover:bg-gray-200 text-gray-800 border border-gray-300"
              }`}
            >
              <RefreshCw className="w-5 h-5" />
              Refresh
            </button>
          </div>

          {/* Entry */}
          <div className={`rounded-xl border mb-6 overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
              <div className="grid grid-cols-12 gap-4 items-end max-w-3xl">
                <div className="col-span-12 md:col-span-8">
                  <label className="text-sm font-medium block mb-1">
                    Tax Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    value={taxName}
                    onChange={(e) => setTaxName(e.target.value)}
                    maxLength={50}
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-black"
                    }`}
                    placeholder="Enter tax name..."
                  />
                </div>

                <div className="col-span-12 md:col-span-4 flex md:justify-end">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                  >
                    <Save className="w-5 h-5" />
                    {saving ? "Saving..." : "Save"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="relative w-full md:max-w-md">
              <Search className="w-4 h-4 absolute left-3 top-3 opacity-60" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={`w-full pl-10 pr-3 py-2 rounded-lg border ${
                  isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-black"
                }`}
                placeholder="Search tax name..."
              />
            </div>

            <div className="text-sm opacity-70">
              Page {page} of {totalPages}
            </div>
          </div>

          {/* Table */}
          <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
            <div className="overflow-x-auto max-w-full">
              <div className="max-h-[60vh] overflow-y-auto">
                <table className="min-w-[520px] w-full">
                  <thead className={`${buttonClass} text-white sticky top-0 z-10`}>
                    <tr>
                      <th className="px-3 py-3 text-center font-semibold border-r border-white/20 whitespace-nowrap">
                        S.No
                      </th>
                      <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">
                        Tax Name
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {loading ? (
                      <tr>
                        <td colSpan={2} className="px-4 py-6 text-center opacity-70">
                          Loading...
                        </td>
                      </tr>
                    ) : rows.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="px-4 py-6 text-center opacity-70">
                          No Records Found
                        </td>
                      </tr>
                    ) : (
                      rows.map((r, idx) => (
                        <tr
                          key={r.fld_taxid ?? idx}
                          className={`transition-colors duration-200 ${
                            idx % 2 === 0
                              ? isDark
                                ? "bg-gray-800"
                                : "bg-gray-50"
                              : isDark
                              ? "bg-gray-900"
                              : "bg-white"
                          }`}
                        >
                          <td className="px-3 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700">
                            {(page - 1) * recordsPerPage + idx + 1}
                          </td>
                          <td className="px-3 py-3 text-sm">
                            {r.fld_taxname}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => page > 1 && fetchList(page - 1, debouncedSearch)}
              disabled={page <= 1}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold border ${
                isDark ? "border-gray-700 bg-gray-800 text-white" : "border-gray-300 bg-white text-gray-800"
              } disabled:opacity-50`}
            >
              <ChevronLeft className="w-4 h-4" />
              Prev
            </button>

            <div className="flex items-center gap-2 flex-wrap justify-center">
              {pageButtons.map((p, i) => (
                <button
                  key={`${p}-${i}`}
                  type="button"
                  disabled={p === "..." || p === page}
                  onClick={() => p !== "..." && fetchList(p, debouncedSearch)}
                  className={`px-3 py-2 rounded-lg text-sm font-semibold border ${
                    p === page
                      ? `${buttonClass} text-white border-transparent`
                      : isDark
                      ? "border-gray-700 bg-gray-800 text-white"
                      : "border-gray-300 bg-white text-gray-800"
                  } disabled:opacity-60`}
                >
                  {p}
                </button>
              ))}
            </div>

            <button
              type="button"
              onClick={() => page < totalPages && fetchList(page + 1, debouncedSearch)}
              disabled={page >= totalPages}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg font-semibold border ${
                isDark ? "border-gray-700 bg-gray-800 text-white" : "border-gray-300 bg-white text-gray-800"
              } disabled:opacity-50`}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default TaxMaster;
